/**
 * Create / edit a bucket (FE-3, PHASE1 §4 buckets). The Transfer/system bucket
 * may be renamed and recolored but not moved to another group or flagged as
 * report-excluded (server returns `system_bucket_immutable` on move/archive).
 *
 * The stateful body is mounted only while open, so state initialises fresh from
 * the current props each time (no reset effect).
 */
import { useMemo, useState } from 'react';
import { Modal } from '../../components/ui/Modal.tsx';
import { InlineError } from '../../components/ui/InlineError.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { Select, type SelectOption } from '../../components/ui/Select.tsx';
import { ApiRequestError } from '../../lib/api.ts';
import { useBucketTree } from '../../lib/hooks/useBuckets.ts';
import { useCreateBucket, useUpdateBucket } from '../../lib/hooks/useBucketMutations.ts';
import type { BucketOut } from '../../lib/types.ts';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export interface BucketFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present → edit mode; absent/null → create mode. */
  bucket?: BucketOut | null;
  /** Preselected group in create mode. */
  defaultGroupId?: number | null;
}

export function BucketFormDialog({
  open,
  onOpenChange,
  bucket,
  defaultGroupId,
}: BucketFormDialogProps) {
  if (!open) return null;
  return (
    <BucketForm
      bucket={bucket ?? null}
      defaultGroupId={defaultGroupId ?? null}
      onClose={() => onOpenChange(false)}
    />
  );
}

function BucketForm({
  bucket,
  defaultGroupId,
  onClose,
}: {
  bucket: BucketOut | null;
  defaultGroupId: number | null;
  onClose: () => void;
}) {
  const isEdit = bucket !== null;
  const isSystem = bucket?.is_system ?? false;
  const groups = useBucketTree(true);
  const create = useCreateBucket();
  const update = useUpdateBucket();

  const groupOptions = useMemo<SelectOption[]>(
    () =>
      groups.map((g) => ({
        value: String(g.id),
        label: g.icon ? `${g.icon} ${g.name}` : g.name,
      })),
    [groups],
  );

  const [name, setName] = useState(() => bucket?.name ?? '');
  const [groupId, setGroupId] = useState<string | undefined>(() =>
    bucket ? String(bucket.group_id) : defaultGroupId != null ? String(defaultGroupId) : undefined,
  );
  const [icon, setIcon] = useState(() => bucket?.icon ?? '');
  const [color, setColor] = useState(() => bucket?.color ?? '');
  const [excluded, setExcluded] = useState(() => bucket?.excluded_from_reports ?? false);
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;

  function handleSubmit() {
    setError(null);
    const trimmedName = name.trim();
    if (trimmedName === '') {
      setError('Bitte einen Namen angeben.');
      return;
    }
    if (!groupId) {
      setError('Bitte eine Gruppe wählen.');
      return;
    }
    const trimmedIcon = icon.trim();
    const trimmedColor = color.trim();

    const onError = (err: unknown) =>
      setError(
        err instanceof ApiRequestError
          ? err.message
          : 'Speichern fehlgeschlagen. Bitte erneut versuchen.',
      );

    if (isEdit && bucket) {
      update.mutate(
        {
          id: bucket.id,
          body: {
            name: trimmedName,
            icon: trimmedIcon === '' ? null : trimmedIcon,
            color: trimmedColor === '' ? null : trimmedColor,
            // System bucket: group + report-exclusion are immutable.
            ...(isSystem ? {} : { group_id: Number(groupId), excluded_from_reports: excluded }),
          },
        },
        { onSuccess: onClose, onError },
      );
    } else {
      create.mutate(
        {
          group_id: Number(groupId),
          name: trimmedName,
          icon: trimmedIcon === '' ? null : trimmedIcon,
          color: trimmedColor === '' ? null : trimmedColor,
          excluded_from_reports: excluded,
        },
        { onSuccess: onClose, onError },
      );
    }
  }

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={isEdit ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={pending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <InlineError message={error} />

        <Field label="Name" required>
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Lebensmittel"
              autoFocus
            />
          )}
        </Field>

        <Field label="Gruppe" required>
          {(id) => (
            <Select
              id={id}
              value={groupId}
              onValueChange={setGroupId}
              options={groupOptions}
              disabled={isSystem}
              placeholder="Gruppe wählen …"
            />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Symbol" hint="Emoji, optional">
            {(id) => (
              <Input
                id={id}
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="🛒"
                maxLength={4}
              />
            )}
          </Field>
          <Field label="Farbe" hint="Hex, optional">
            {(id) => (
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label="Farbe wählen"
                  value={HEX_RE.test(color) ? color : '#546e7a'}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-9 shrink-0 cursor-pointer rounded-md border border-slate-200 bg-white transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:bg-slate-900 dark:focus-visible:outline-sky-500"
                />
                <Input
                  id={id}
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#EF6C00"
                />
              </div>
            )}
          </Field>
        </div>

        {!isSystem ? (
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={excluded}
              onChange={(e) => setExcluded(e.target.checked)}
              className="h-4 w-4 rounded border border-slate-200 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:accent-sky-500 dark:focus-visible:outline-sky-500"
            />
            Von Auswertungen ausschließen
          </label>
        ) : null}
      </div>
    </Modal>
  );
}
